import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../../config/api';
import '../css/InformationForm.css';
import InputField from '../components/InputField.jsx';
import SelectField from '../components/SelectField.jsx';
import RegistrationPopup from '../../../components/common/RegistrationPopup.jsx';
import { isValidEmail } from '../../../utils/validationUtils';
import {
  UserIcon,
  EmailIcon,
  PhoneIcon,
  BuildingIcon,
  VATIcon,
  PasswordIcon,
  BankIcon,
  AccountIcon
} from '../components/Icons.jsx';

const InformationForm = () => {
  const nav = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    companyName: '',
    organVAT: '',
    password: '',
    userType: 'customer',
    bankName: '',
    bankAccountNumber: '',
    bankAccountName: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    companyName: '',
    organVAT: '',
    password: '',
    userType: '',
    bankName: '',
    bankAccountNumber: '',
    bankAccountName: '',
    server: '',
  });
  const [banks, setBanks] = useState([]);
  const [popup, setPopup] = useState({
    isOpen: false,
    type: '',
    title: '',
    message: '',
    subaccountCode: '',
    isRealAccount: false
  });

  const numberRegex = /^\d*$/;

  const closePopup = () => {
    setPopup(prev => ({ ...prev, isOpen: false }));
  };

  useEffect(() => {
    const email = localStorage.getItem('reg_email') || '';
    const password = localStorage.getItem('reg_password') || '';
    setFormData(prev => ({ ...prev, email, password }));
    
    // Fetch banks for organizer registration
    fetchBanks();
  }, []);

  const fetchBanks = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/payments/banks`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setBanks(data.banks || []);
        }
      }
    } catch (error) {
      console.error('Error fetching banks:', error);
    }
  };


  const handleChange = (e) => {
    const { name, value } = e.target;

    if (['phoneNumber', 'organVAT', 'bankAccountNumber'].includes(name)) {
      if (value === '' || numberRegex.test(value)) {
        setFormData(prev => ({ ...prev, [name]: value }));
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: name === 'email' ? value.toLowerCase() : value }));
    }

    const newErrors = { ...errors };
    switch (name) {
      case 'name':
        newErrors.name = value ? '' : 'Full name is required.';
        break;
      case 'email':
        newErrors.email = !value || !isValidEmail(value) ? 'Valid email required (e.g., example@domain.com).' : '';
        break;
      case 'phoneNumber':
        if (!value) newErrors.phoneNumber = 'Phone number is required.';
        else if (!numberRegex.test(value)) newErrors.phoneNumber = 'Phone number must contain only digits.';
        else if (value.length !== 10) newErrors.phoneNumber = 'Must be 10 digits.';
        else newErrors.phoneNumber = '';
        break;
      case 'companyName':
        newErrors.companyName = '';
        // Clear VAT error when company name is cleared
        if (!value && !formData.organVAT) {
          newErrors.organVAT = '';
        }
        break;
      case 'organVAT':
        // VAT number is mandatory if company name is provided
        if (formData.companyName && !value) {
          newErrors.organVAT = 'VAT number is required when company name is provided.';
        } else if (value && !numberRegex.test(value)) {
          newErrors.organVAT = 'Must be 9 digits';
        } else if (value && value.length !== 9) {
          newErrors.organVAT = 'Must be 9 digits';
        } else {
          newErrors.organVAT = '';
        }
        break;
      case 'password':
        newErrors.password = value ? '' : 'Password is required.';
        break;
      case 'userType':
        newErrors.userType = '';
        // Clear banking errors when switching to customer
        if (value === 'customer') {
          newErrors.bankName = '';
          newErrors.bankAccountNumber = '';
          newErrors.bankAccountName = '';
        }
        break;
      case 'bankName':
        newErrors.bankName = formData.userType === 'organizer' && !value ? 'Bank is required for organizers.' : '';
        break;
      case 'bankAccountNumber':
        if (formData.userType === 'organizer' && !value) {
          newErrors.bankAccountNumber = 'Account number is required for organizers.';
        } else if (value && !numberRegex.test(value)) {
          newErrors.bankAccountNumber = 'Account number must contain only digits.';
        } else if (value && (value.length < 10 || value.length > 11)) {
          newErrors.bankAccountNumber = 'Account number must be 10-11 digits.';
        } else {
          newErrors.bankAccountNumber = '';
        }
        break;
      case 'bankAccountName':
        newErrors.bankAccountName = formData.userType === 'organizer' && !value ? 'Account name is required for organizers.' : '';
        break;
      default:
        break;
    }
    setErrors(newErrors);
  };

  const validateForm = () => {
    const newErrors = {
      name: '',
      email: '',
      phoneNumber: '',
      companyName: '',
      organVAT: '',
      password: '',
      userType: '',
      bankName: '',
      bankAccountNumber: '',
      bankAccountName: '',
      server: '',
    };

    if (!formData.name) newErrors.name = 'Full name is required.';
    if (!formData.email || !isValidEmail(formData.email)) newErrors.email = 'Valid email required (e.g., example@domain.com).';
    if (!formData.password) newErrors.password = 'Password is required.';
    if (!formData.phoneNumber) newErrors.phoneNumber = 'Phone number is required.';
    if (!numberRegex.test(formData.phoneNumber) || formData.phoneNumber.length !== 10) newErrors.phoneNumber = 'Must be 10 digits.';
    
    // VAT number is mandatory if company name is provided
    if (formData.companyName && !formData.organVAT) {
      newErrors.organVAT = 'VAT number is required when company name is provided.';
    } else if (formData.organVAT && !numberRegex.test(formData.organVAT)) {
      newErrors.organVAT = 'Must be 9 digits';
    } else if (formData.organVAT && formData.organVAT.length !== 9) {
      newErrors.organVAT = 'Must be 9 digits';
    }

    // Banking details are required for organizers
    if (formData.userType === 'organizer') {
      if (!formData.bankName) newErrors.bankName = 'Bank is required for organizers.';
      if (!formData.bankAccountNumber) {
        newErrors.bankAccountNumber = 'Account number is required for organizers.';
      } else if (!numberRegex.test(formData.bankAccountNumber)) {
        newErrors.bankAccountNumber = 'Account number must contain only digits.';
      } else if (formData.bankAccountNumber.length < 10 || formData.bankAccountNumber.length > 11) {
        newErrors.bankAccountNumber = 'Account number must be 10-11 digits.';
      }
      if (!formData.bankAccountName) newErrors.bankAccountName = 'Account name is required for organizers.';
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsSubmitting(true);
    setErrors(prev => ({ ...prev, server: '' }));

    const validationErrors = validateForm();
    if (Object.values(validationErrors).some(error => error !== '')) {
      setErrors(validationErrors);
      setIsSubmitting(false);
      return;
    }

    const userData = {
      name: formData.name,
      email: formData.email.toLowerCase(),
      password: formData.password,
      phone: formData.phoneNumber,
      company_name: formData.companyName,
      vat_number: formData.organVAT,
      user_type: formData.userType,
      bank_name: formData.userType === 'organizer' ? formData.bankName : undefined,
      bank_account_number: formData.userType === 'organizer' ? formData.bankAccountNumber : undefined,
      bank_account_name: formData.userType === 'organizer' ? formData.bankAccountName : undefined,
    };

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/shared/update-profile`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(userData),
      });
      const data = await res.json();
      
      if (!res.ok) {
        // Handle server errors
        if (data.error) {
          throw new Error(data.details || data.error || 'Error updating profile');
        }
        throw new Error(data.message || 'Error updating profile');
      }
      
      // Show success popup
      setPopup({
        isOpen: true,
        type: 'success',
        title: 'Profile Completed Successfully!',
        message: 'Your profile has been updated. Redirecting to main menu...',
        onOk: () => {
          nav('/eventmenu');
        }
      });
      
    } catch (error) {
      console.error('Profile update error:', error);
      setErrors(prev => ({ ...prev, server: error.message }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const isSubmitButtonDisabled = isSubmitting;

  return (
    <div className="page-container">
      <div className="back-header" onClick={() => nav('/')}>← Back</div>
      <h1 className="page-title">Complete Your Profile</h1>
      <div className="form-container">
        {errors.server && (
          <div className="error-server">{errors.server}</div>
        )}
        <form onSubmit={handleSubmit}>
          <div className="form-grid">
            <InputField
              label="Full name"
              Icon={UserIcon}
              type="text"
              name="name"
              placeholder="Enter your full name"
              value={formData.name}
              onChange={handleChange}
              disabled={isSubmitting}
              autoComplete="name"
              error={errors.name}
            />

            <InputField
              label="Email"
              Icon={EmailIcon}
              type="email"
              name="email"
              placeholder="example.email@domain.com"
              value={formData.email}
              onChange={handleChange}
              disabled={true}
              autoComplete="email"
              error={errors.email}
            />

            <InputField
              label="Phone Number"
              Icon={PhoneIcon}
              type="tel"
              name="phoneNumber"
              placeholder="Enter 10-digit Phone Number"
              value={formData.phoneNumber}
              onChange={handleChange}
              disabled={isSubmitting}
              autoComplete="tel"
              maxLength={10}
              error={errors.phoneNumber}
            />

            <SelectField
              label="Account Type"
              Icon={UserIcon}
              name="userType"
              value={formData.userType}
              onChange={handleChange}
              disabled={isSubmitting}
              error={errors.userType}
            >
              <option value="">Select account type</option>
              <option value="customer">Customer</option>
              <option value="organizer">Organizer</option>
            </SelectField>

            {formData.userType === 'organizer' && (
              <>
                <InputField
                  label="Bank Name"
                  type="text"
                  name="bankName"
                  placeholder="Select your bank"
                  value={formData.bankName}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  error={errors.bankName}
                />

                <InputField
                  label="Account Number"
                  type="text"
                  name="bankAccountNumber"
                  placeholder="Enter bank account number"
                  value={formData.bankAccountNumber}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  maxLength={11}
                  error={errors.bankAccountNumber}
                />

                <InputField
                  label="Account Name"
                  type="text"
                  name="bankAccountName"
                  placeholder="Enter account holder name"
                  value={formData.bankAccountName}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  error={errors.bankAccountName}
                />
              </>
            )}

            <InputField
              label="Company Name (Optional)"
              Icon={BuildingIcon}
              type="text"
              name="companyName"
              placeholder="Enter your company name"
              value={formData.companyName}
              onChange={handleChange}
              disabled={isSubmitting}
              autoComplete="organization"
              error={errors.companyName}
            />

            <InputField
              label="VAT Number (Optional)"
              Icon={VATIcon}
              type="text"
              name="organVAT"
              placeholder="Enter VAT number"
              value={formData.organVAT}
              onChange={handleChange}
              disabled={isSubmitting}
              maxLength={9}
              error={errors.organVAT}
            />

            <InputField
              label="Password"
              Icon={PasswordIcon}
              type="password"
              name="password"
              placeholder="Enter your password"
              value={formData.password}
              onChange={handleChange}
              disabled={isSubmitting}
              autoComplete="current-password"
              error={errors.password}
            />
          </div>

          <div className="submit-container">
            <button type="submit" className="submit-btn" disabled={isSubmitButtonDisabled}>
              {isSubmitting ? 'Processing...' : 'Complete Registration'}
            </button>
          </div>
        </form>
      </div>
      
      <RegistrationPopup
        isOpen={popup.isOpen}
        onClose={closePopup}
        type={popup.type}
        title={popup.title}
        message={popup.message}
        subaccountCode={popup.subaccountCode}
        isRealAccount={popup.isRealAccount}
      />
    </div>
  );
};

export default InformationForm;
